import localforage from 'localforage';
import cloneDeep from 'lodash.clonedeep';
import Analytics from '@/mixins/Analytics';
import { decodeJwt } from '@/utilities';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';
import { markStorageReady } from '@/utilities/resolvers/storageReadyResolver';
import { getLocalStorageKeySizes, getStorageEstimate } from '@/utilities/localStorageHandler';
import IdentityClient from '../../api/IdentityClient';
import User from '../../models/User';

export const USER_STORAGE_KEY = 'acv-user-storage';

const filteredMutations = new Set([
  'setUser',
  'setUserSettings',
  'setCarfaxTokens',
  'setUserAppliedSavedSearchIds',
]);

// exported plugin which saves to local storage for user related context
const userStoragePlugin = (store) => {
  store.subscribe((mutation, state) => {
    if (!filteredMutations.has(mutation.type)) return;

    const authorizationState = cloneDeep(state.authorization);
    delete authorizationState.refreshTokenRequest;
    delete authorizationState.errors;

    localforage
      .setItem(USER_STORAGE_KEY, { authorization: authorizationState })
      .catch(async (err) => {
        Analytics.methods.noticeError(err, {
          context: 'userStorage.saveState',
          key: USER_STORAGE_KEY,
          errorName: err?.name,
          writeBytes: new Blob([JSON.stringify(authorizationState)]).size,
          keyBytes: getLocalStorageKeySizes(),
          storageEstimate: await getStorageEstimate(),
        });
      });
  });
};

// Reads the persisted authorization state from IndexedDB,
// refreshes JWT tokens if they're close to expiring,
// and returns the hydrated state to merge into the store
export const restoreUserStorage = async (defaultAuthorizationState) => {
  let authorizationStorage;

  try {
    const storage = await localforage.getItem(USER_STORAGE_KEY);
    authorizationStorage = storage
      ? cloneDeep(storage)
      : { authorization: defaultAuthorizationState };

    const {
      authorization: { user: indexeddbUser },
    } = authorizationStorage;

    try {
      let jwtToken = '';
      let refreshToken = '';
      if (window.localStorage) {
        const tokenStorage = window.localStorage.getItem('acv-jwt-tokens');
        try {
          jwtToken = JSON.parse(tokenStorage).jwtToken;
          refreshToken = JSON.parse(tokenStorage).refreshToken;
        } catch (err) {
          Analytics.methods.noticeError(err, {
            context: 'userStorage.restoreState.parseTokens',
          });
        }
      }

      const decodedJwtToken = decodeJwt(jwtToken) ?? '';
      const jwtExpirationVal = new Date(0);
      jwtExpirationVal.setUTCSeconds(decodedJwtToken?.exp ?? 0);
      const currentDate = new Date();
      const jwtDifferenceInMinutes = getDifferenceInMinutes(
        jwtExpirationVal, // start date
        currentDate, // end date
      );
      const tokensCloseToExpiration = jwtDifferenceInMinutes < 3;
      if (tokensCloseToExpiration) {
        const identityClient = new IdentityClient(indexeddbUser);
        let refreshResponse;

        if (refreshToken !== '' && !window.acvNative) {
          refreshResponse = await identityClient.refreshToken({
            jwtToken,
            refreshToken,
          });
        }
        const newTokens = refreshResponse.data || {};
        window.localStorage.setItem(
          'acv-jwt-tokens',
          JSON.stringify({
            ...newTokens,
            lastUpdated: new Date(),
          }),
        );
      }
    } catch (err) {
      Analytics.methods.noticeError(err, {
        context: 'userStorage.restoreState.tokenRefresh',
        key: USER_STORAGE_KEY,
        errorName: err?.name,
        keyBytes: getLocalStorageKeySizes(),
        storageEstimate: await getStorageEstimate(),
      });
    }

    const user = new User(indexeddbUser);
    authorizationStorage.authorization.user = user;

    // the user initialization used to happen in auth module
    // in migrateJwtTokensToStorage method. although the method
    // is no longer necessary, we still need to initialize
    // the user to get session tokens and have a userId in state
    const {
      authorization: { jwtTokenExpirationDateTime },
    } = authorizationStorage;
    if (jwtTokenExpirationDateTime) {
      authorizationStorage.authorization.jwtTokenExpirationDateTime = new Date(
        jwtTokenExpirationDateTime,
      );
    }
  } catch (err) {
    Analytics.methods.noticeError(err, { context: 'userStorage.restoreState' });
    authorizationStorage = { authorization: defaultAuthorizationState };
  } finally {
    markStorageReady(); // Trigger the promise resolve so storage is ready in router
  }

  return authorizationStorage;
};

export default { plugin: userStoragePlugin };
